/**
 * Couche service « Plateforme » (gestion multi-école), réservée au super-administrateur :
 * écoles (totaux uniquement, aucune donnée nominative) et statistiques comparatives.
 */
import { assetUrl, http, orUndefined } from "./http";
import type { CycleCode, PlatformStat, School, SchoolPlan } from "@/types";

interface ApiPlatformSchool {
  id: number;
  name: string;
  shortName: string | null;
  code: string;
  isActive: boolean;
  city: string | null;
  country: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  slogan: string | null;
  logoUrl: string | null;
  schoolYear: string | null;
  createdAt: string;
  cycles: CycleCode[];
  studentsCount: number;
  staffCount: number;
  usersCount: number;
  plan: SchoolPlan;
  subscriptionEndsAt: string | null;
}

interface ApiStat extends Omit<PlatformStat, "schoolId"> {
  schoolId: number;
}

function toSchool(s: ApiPlatformSchool): School {
  return {
    id: String(s.id),
    name: s.name,
    shortName: s.shortName ?? s.name,
    code: s.code,
    city: s.city ?? "",
    country: s.country ?? "",
    address: s.address ?? "",
    phone: s.phone ?? "",
    email: s.email ?? "",
    slogan: s.slogan ?? "",
    logo: assetUrl(s.logoUrl),
    schoolYear: s.schoolYear ?? "",
    cycles: s.cycles,
    studentsCount: s.studentsCount,
    staffCount: s.staffCount,
    usersCount: s.usersCount,
    isActive: s.isActive,
    plan: s.plan ?? "ETABLISSEMENT",
    subscriptionEndsAt: s.subscriptionEndsAt ?? null,
    createdAt: s.createdAt,
  };
}

/** Champs modifiables d'une école. */
export interface SchoolDto {
  name: string;
  shortName: string;
  city: string;
  country: string;
  address: string;
  phone: string;
  email: string;
  slogan: string;
  plan: SchoolPlan;
  /** Dernier jour de l'abonnement (AAAA-MM-JJ) ; vide : sans échéance. */
  subscriptionEndsAt: string;
}

/** Ouverture d'une école : code et cycles sont fixés à la création. */
export interface CreateSchoolDto extends SchoolDto {
  code: string;
  cycles: CycleCode[];
  /** Administrateur (directeur) de l'école, créé avec un mot de passe provisoire. */
  admin?: { firstName: string; lastName: string; email: string };
}

/** Compte administrateur créé : le mot de passe provisoire n'est connu qu'à cet instant. */
export interface CreatedAdmin {
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  /** true : les identifiants ont aussi été envoyés par e-mail. */
  emailSent: boolean;
}

function fieldsBody(dto: Partial<SchoolDto>) {
  return {
    name: dto.name?.trim(),
    shortName: orUndefined(dto.shortName),
    city: orUndefined(dto.city),
    country: orUndefined(dto.country),
    address: orUndefined(dto.address),
    phone: orUndefined(dto.phone),
    email: orUndefined(dto.email),
    slogan: orUndefined(dto.slogan),
    plan: dto.plan,
    // Chaîne vide : sans échéance (null efface la date).
    subscriptionEndsAt: dto.subscriptionEndsAt === undefined ? undefined : dto.subscriptionEndsAt || null,
  };
}

export function toSchoolModel(s: unknown): School {
  return toSchool(s as ApiPlatformSchool);
}

/** Liste toutes les écoles de la plateforme. */
export async function listSchools(): Promise<School[]> {
  return (await http.get<ApiPlatformSchool[]>("/platform/schools")).map(toSchool);
}

/** Ouvre une école (cycles, année active, rôles de départ) et, au besoin, son administrateur. */
export async function createSchool(
  dto: CreateSchoolDto,
): Promise<{ school: School; admin: CreatedAdmin | null }> {
  const res = await http.post<{ school: ApiPlatformSchool; admin: CreatedAdmin | null }>(
    "/platform/schools",
    {
      ...fieldsBody(dto),
      code: dto.code.trim().toUpperCase(),
      cycles: dto.cycles,
      admin: dto.admin,
    },
  );
  return { school: toSchool(res.school), admin: res.admin };
}

/** Met à jour une école (coordonnées, activation). Une école désactivée ne peut plus se connecter. */
export async function updateSchool(
  id: string,
  dto: Partial<SchoolDto> & { isActive?: boolean },
): Promise<School> {
  return toSchool(
    await http.put<ApiPlatformSchool>(`/platform/schools/${id}`, {
      ...fieldsBody(dto),
      isActive: dto.isActive,
    }),
  );
}

/** Statistiques comparatives des écoles (totaux uniquement). */
export async function getPlatformStats(): Promise<PlatformStat[]> {
  return (await http.get<ApiStat[]>("/platform/stats")).map((s) => ({
    ...s,
    schoolId: String(s.schoolId),
  }));
}
